import { Alert, Button, Tooltip } from '@billda/design-system';
import { EditIcon } from '@billda/design-system';
import { useCallback, useMemo } from 'react';
import { GangParticipant } from '../../types/gangCreationTypes';
import { PermissionGate } from '../../../../shared/components/gates/PermissionGate';

interface GangFinancialSummaryProps {
  leaders: GangParticipant[];
  members: GangParticipant[];
  isEqualSplit?: boolean;
  title?: string;
  testIdPrefix?: string;
  testIdSuffix?: string;
  showEditButton?: boolean;
  onEditClick?: () => void;
  editButtonTooltip?: string;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

export const GangFinancialSummary = ({
  leaders,
  members,
  isEqualSplit = false,
  title = 'Financial Summary',
  testIdPrefix,
  testIdSuffix,
  showEditButton = false,
  onEditClick,
  editButtonTooltip = 'Edit member configuration',
}: GangFinancialSummaryProps) => {
  const allMembers = useMemo(
    () => [...leaders, ...members],
    [leaders, members]
  );

  // When equal split is enabled, count by paymentType instead of stored percentage
  const membersWithPercentageSplit = useMemo(
    () =>
      allMembers.filter(member =>
        isEqualSplit
          ? member.paymentType === 'percentage'
          : member.defaultSplitPercentage !== null &&
            member.defaultSplitPercentage !== undefined
      ),
    [allMembers, isEqualSplit]
  );

  const membersWithFixedRate = useMemo(
    () =>
      allMembers.filter(
        member =>
          member.defaultRate !== null && member.defaultRate !== undefined
      ),
    [allMembers]
  );

  // When equal split is enabled, total is always 100%
  const totalPercentageSplit = useMemo(
    () =>
      isEqualSplit && membersWithPercentageSplit.length > 0
        ? 100
        : membersWithPercentageSplit.reduce(
            (sum, member) => sum + (member.defaultSplitPercentage || 0),
            0
          ),
    [membersWithPercentageSplit, isEqualSplit]
  );

  const totalFixedRate = useMemo(
    () =>
      membersWithFixedRate.reduce((sum, member) => {
        const rate = member.defaultRate || 0;
        const units = member.defaultUnitsValue || 0;
        return sum + rate * units;
      }, 0),
    [membersWithFixedRate]
  );

  const hasFinancialData = useMemo(
    () =>
      membersWithPercentageSplit.length > 0 || membersWithFixedRate.length > 0,
    [membersWithPercentageSplit.length, membersWithFixedRate.length]
  );

  const buildTestId = useCallback(
    (base: string) => `${testIdPrefix ?? ''}${base}${testIdSuffix ?? ''}`,
    [testIdPrefix, testIdSuffix]
  );

  return (
    <PermissionGate permission="gangs.manage">
      <div
        className="mt-4"
        data-testid={buildTestId('financial-summary-container')}
      >
        <div className="mb-4 flex items-center justify-between gap-2">
          <h6
            className="type-h6 flex-1 text-content-accent"
            data-testid={buildTestId('financial-summary-title')}
          >
            {title}
          </h6>
          {showEditButton ? (
            <Tooltip content={editButtonTooltip}>
              <Button
                variant="ghost"
                size="icon"
                onClick={onEditClick}
                aria-label={editButtonTooltip}
                data-testid={buildTestId('financial-summary-edit-button')}
                className="size-8"
              >
                <EditIcon aria-hidden="true" className="size-4" />
              </Button>
            </Tooltip>
          ) : null}
        </div>

        {hasFinancialData ? (
          <>
            <div className="mt-4 flex flex-col gap-8 sm:flex-row">
              {membersWithPercentageSplit.length > 0 && (
                <div
                  data-testid={buildTestId(
                    'financial-summary-split-allocation-container'
                  )}
                >
                  <span
                    className="type-body-md block text-content-secondary"
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-label'
                    )}
                  >
                    Total Split Allocated
                  </span>
                  <span
                    className={`type-h4 block ${
                      totalPercentageSplit === 100
                        ? 'text-content-success'
                        : 'text-content-warning'
                    }`}
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-value'
                    )}
                    data-percentage={Number(totalPercentageSplit.toFixed(2))}
                  >
                    {totalPercentageSplit.toFixed(2)}%
                  </span>
                  <span
                    className="type-caption block text-content-secondary"
                    data-testid={buildTestId(
                      'financial-summary-split-allocation-count'
                    )}
                    data-count={membersWithPercentageSplit.length}
                  >
                    {membersWithPercentageSplit.length} member
                    {membersWithPercentageSplit.length === 1 ? '' : 's'} with
                    splits
                  </span>
                </div>
              )}

              {membersWithFixedRate.length > 0 && (
                <div
                  data-testid={buildTestId(
                    'financial-summary-fixed-rates-container'
                  )}
                >
                  <span
                    className="type-body-md block text-content-secondary"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-label'
                    )}
                  >
                    Total Fixed Rates
                  </span>
                  <span
                    className="type-h4 block text-content-secondary"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-value'
                    )}
                    data-total-fixed-rate={totalFixedRate}
                  >
                    {formatCurrency(totalFixedRate)}
                  </span>
                  <span
                    className="type-caption block text-content-secondary"
                    data-testid={buildTestId(
                      'financial-summary-fixed-rates-count'
                    )}
                    data-count={membersWithFixedRate.length}
                  >
                    {membersWithFixedRate.length} member
                    {membersWithFixedRate.length === 1 ? '' : 's'} with fixed
                    rate{membersWithFixedRate.length === 1 ? '' : 's'}
                  </span>
                </div>
              )}
            </div>

            {totalPercentageSplit > 0 && totalPercentageSplit !== 100 && (
              <Alert
                severity="warning"
                className="mt-4"
                data-testid={buildTestId(
                  'financial-summary-split-allocation-warning'
                )}
              >
                Default percentage splits total{' '}
                {totalPercentageSplit.toFixed(2)}
                %. Configuring the gang so that the full 100% is allocated will
                help speed up the pot splitting process.
              </Alert>
            )}
          </>
        ) : (
          <Alert
            severity="info"
            className="mt-4"
            data-testid={buildTestId('financial-summary-empty-message')}
          >
            No financial information has been configured for this gang yet.
          </Alert>
        )}
      </div>
    </PermissionGate>
  );
};
